import type { Insertable } from 'kysely';
import type { AppID, AppTenantAccess, FacilityGroupID, FacilityID, TenantID, UserID } from '../types/db.ts';
import type { ISO8601Timestamp } from '../types/common.ts';

/**
 * The apps a newly created tenant is granted.
 */
export const appAccessDefaultsForNewTenant: Record<'demo' | 'normal', Record<AppID, boolean>> = {
  // Demo tenants get absolutely nothing, they have to talk to us first:
  demo: {
    tri: false,
    emissions: false,
    tierii: false,
    hazwaste: false,
    compliance: false,
    parsing: false,
    determinations: false,
  },
  normal: {
    tri: true,
    emissions: true,
    tierii: true,
    hazwaste: false, // TODO: 1. Hazwaste - Once the app is live, this should likely flip to true.
    compliance: false,
    parsing: false,
    determinations: false,
  },
};

/** The tenant-level grants to insert alongside a newly created tenant. */
export function appTenantAccessDefaultRows(
  tenantID: TenantID,
  isDemo: boolean,
  createdBy: UserID,
): Insertable<AppTenantAccess>[] {
  const defaults = appAccessDefaultsForNewTenant[isDemo ? 'demo' : 'normal'];
  return (Object.keys(defaults) as AppID[])
    .filter((appID) => defaults[appID])
    .map((appID) => ({ tenantID, appID, isGrantedAccess: true, createdBy }));
}

/**
 * App access is configured at three levels: tenant → facility group → facility. The most specific setting wins, so
 * granting at the tenant level cascades down to every group and facility, and a facility can opt back out with an
 * explicit denial. When nothing in the chain is set, access is denied.
 */
export type AppAccessLevel = 'tenant' | 'group' | 'facility';

export type AppAccessTarget =
  | { level: 'tenant'; tenantID: TenantID }
  | { level: 'group'; facilityGroupID: FacilityGroupID }
  | { level: 'facility'; facilityID: FacilityID };

/** A setting stored explicitly at one level. null means nothing is set at that level, so it inherits. */
export type AppAccessSetting = {
  isGrantedAccess: boolean;
  createdAt: ISO8601Timestamp;
  createdByName: string;
} | null;

export type AppAccessSettings = Record<AppID, AppAccessSetting>;

export interface AppAccessEffective {
  isGrantedAccess: boolean;
  /** The level whose setting decided the answer, or null when nothing in the chain is set and access is denied. */
  decidedBy: AppAccessLevel | null;
}

/**
 * Resolves effective access from a chain of settings ordered from least specific (tenant) to most specific.
 */
export function appAccessResolve(
  chain: { level: AppAccessLevel; setting: AppAccessSetting | boolean | null }[],
): AppAccessEffective {
  for (let i = chain.length - 1; i >= 0; i--) {
    const { level, setting } = chain[i];
    if (setting === null) {
      continue;
    }

    return { isGrantedAccess: typeof setting === 'boolean' ? setting : setting.isGrantedAccess, decidedBy: level };
  }

  return { isGrantedAccess: false, decidedBy: null };
}

export function appAccessTargetKey(target: AppAccessTarget): string {
  switch (target.level) {
    case 'tenant':
      return `tenant-${target.tenantID}`;
    case 'group':
      return `group-${target.facilityGroupID}`;
    case 'facility':
      return `facility-${target.facilityID}`;
  }
}

export function appAccessSettingsEmpty(appIDs: AppID[]): AppAccessSettings {
  const result = {} as AppAccessSettings;
  for (const appID of appIDs) {
    result[appID] = null;
  }

  return result;
}

export interface AppAccessOverview {
  apps: { appID: AppID; name: string }[];
  tenants: { tenantID: TenantID; name: string; isDemo: boolean; settings: AppAccessSettings }[];
  groups: { facilityGroupID: FacilityGroupID; tenantID: TenantID; name: string; settings: AppAccessSettings }[];
  facilities: {
    facilityID: FacilityID;
    facilityGroupID: FacilityGroupID;
    tenantID: TenantID;
    name: string;
    settings: AppAccessSettings;
  }[];
}
